import React from 'react';

import RealEstateProofJpg from './real-estate-proof.jpg';
import RealEstateProofWebp from './real-estate-proof.webp';
import RealEstateProofMobileJpg from './real-estate-proof-mobile.jpg';
import RealEstateProofMobileWebp from './real-estate-proof-mobile.webp';
import PaperAndPenPng from './paper-and-pen.png';
import PaperAndPenWebp from './paper-and-pen.webp';
import CalendarPng from './calendar.png';
import CalendarWebp from './calendar.webp';
import PersonPng from './person.png';
import PersonWebp from './person.webp';

import { Modal } from '../../../../common/display/modal/Modal';
import { Benefit } from '../../../../../@types/benefits';
import { BenefitModalProps } from '../../../../../@types/benefitModalProps';
import { APP_CONFIG } from '../../../../../config';
import { BuyerPlusLogo } from '../../../../common/display/buyerPlusLogo/BuyerPlusLogo';
import { OrderedList } from '../../../../common/display/orderedList/OrderedList';
import { ImageSource } from '../../../../common/display/imageSource/ImageSource';
import { FAQList } from '../../../../common/display/faqTab/faqList/FAQList';
import { CustomerService } from '../../../../common/display/faqTab/customerService/CustomerService';
import { Msg, useIntl } from '@/i18n';

const styles = {
  overviewTab: { maxWidth: 530 },
};

const OverviewTab = () => (
  <div style={styles.overviewTab}>
    <div className='font-heading-xsmall-regular padding-bottom-l'>
      <Msg id='benefits.realEstateProof.modal.overview.paragraph1' defaultMessage='Safe house construction...' />
    </div>
    <div className='padding-bottom-l'>
      <Msg id='benefits.realEstateProof.modal.overview.paragraph2' defaultMessage='A neutral expert will help you...' />
    </div>
    <ul className='list-check list-spacing-s'>
      <li>
        <Msg
          id='benefits.realEstateProof.modal.overview.bullet1'
          defaultMessage='15% discount for KäuferPlus'
          values={{ BuyerPlusLogo: <BuyerPlusLogo /> }}
        />
      </li>
      <li>
        <Msg id='benefits.realEstateProof.modal.overview.bullet2' defaultMessage='Supervision by DEKRA experts' />
      </li>
      <li>
        <Msg id='benefits.realEstateProof.modal.overview.bullet3' defaultMessage='On-site inspections...' />
      </li>
      <li>
        <Msg id='benefits.realEstateProof.modal.overview.bullet4' defaultMessage='Control of the execution status' />
      </li>
      <li>
        <Msg id='benefits.realEstateProof.modal.overview.bullet5' defaultMessage='Preparation of reports...' />
      </li>
      <li>
        <Msg id='benefits.realEstateProof.modal.overview.bullet6' defaultMessage='Only for new construction...' />
      </li>
    </ul>
  </div>
);

const HowItWorksTab = () => {
  const { t } = useIntl();
  const Item = OrderedList.Item;

  return (
    <div className='margin-bottom-xxl'>
      <OrderedList>
        <Item
          image={
            <ImageSource
              image={PaperAndPenPng}
              imageWebp={PaperAndPenWebp}
              imageAlt={t({
                id: 'benefits.realEstateProof.modal.howItWorks.image1',
                defaultMessage: 'Paper and pen',
              })}
              sizes='100px'
            />
          }
          text={t({
            id: 'benefits.realEstateProof.modal.howItWorks.paragraph1',
            defaultMessage: 'Get help from Dekra',
          })}
        />
        <Item
          image={
            <ImageSource
              image={CalendarPng}
              imageWebp={CalendarWebp}
              imageAlt={t({
                id: 'benefits.realEstateProof.modal.howItWorks.image2',
                defaultMessage: 'Calendar',
              })}
              sizes='100px'
            />
          }
          text={t({
            id: 'benefits.realEstateProof.modal.howItWorks.paragraph2',
            defaultMessage: 'Get appointment and consultation',
          })}
        />
        <Item
          image={
            <ImageSource
              image={PersonPng}
              imageWebp={PersonWebp}
              imageAlt={t({
                id: 'benefits.realEstateProof.modal.howItWorks.image3',
                defaultMessage: 'Person',
              })}
              sizes='100px'
            />
          }
          text={t({
            id: 'benefits.realEstateProof.modal.howItWorks.paragraph3',
            defaultMessage: 'Get your personal offer',
          })}
        />
      </OrderedList>
    </div>
  );
};

const Faq = () => (
  <div>
    <div className='font-heading-xsmall-regular padding-bottom-l'>
      <div role='heading' aria-level={5}>
        <Msg id='benefits.realEstateProof.modal.faqHeadline1' defaultMessage='General use-cases' />
      </div>
    </div>
    <FAQList benefit='realEstateProof' start={0} end={4} />
    <div className='font-heading-xsmall-regular padding-bottom-l'>
      <div role='heading' aria-level={5}>
        <Msg id='benefits.realEstateProof.modal.faqHeadline2' defaultMessage='Costs & procedure' />
      </div>
    </div>
    <FAQList benefit='realEstateProof' start={5} end={11} />
    <CustomerService />
  </div>
);

const buttonArea = (
  <div className='align-center'>
    <a
      className='button-secondary button-rounded'
      href={`${APP_CONFIG.buyingPageUrl}?modal=realEstateProof&cmp_id=10-051668&cmp_name=myscout_buying_buy&cmp_position=residential_benefits_buy&cmp_creative=property_proof_cta`}
    >
      <Msg id='benefits.realEstateProof.modal.linkButton' defaultMessage='Request offer' />
    </a>
  </div>
);

export const RealEstateProof = ({ isVisible, onClose }: BenefitModalProps) => {
  const { t } = useIntl();

  return (
    <Modal
      title={t({ id: 'benefits.realEstateProof.headline', defaultMessage: 'DEKRA Real estate proof' })}
      isVisible={isVisible}
      image={RealEstateProofJpg}
      imageWebp={RealEstateProofWebp}
      imageMobile={RealEstateProofMobileJpg}
      imageMobileWebp={RealEstateProofMobileWebp}
      imageUseFullWidth={true}
      imageAlt={t({
        id: 'benefits.realEstateProof.modal.image',
        defaultMessage: 'Man inspecting building',
      })}
      tabsContent={[<OverviewTab key='overview' />, <HowItWorksTab key='howItWorks' />, <Faq key='faq' />]}
      buttonArea={buttonArea}
      benefit={Benefit.REAL_ESTATE_PROOF}
      onClose={onClose}
    />
  );
};
